import React from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
} from "@material-tailwind/react";
import { XMarkIcon, CloudArrowUpIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Toaster from "@/components/Toaster";
import FilePreview from "@/components/FilePreview";
import ShowLoader from "@/components/Loader";
import { useTranslation } from "react-i18next";

// Validation Schema supporting PDF and Images
const schema = (t) => yup.object().shape({
    payment_slip: yup
        .mixed()
        .required(t('payment_slip_required'))
        .test("fileType", t('file_type_not_allowed'), (value) => {
            if (!value) return false;
            const validTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
            return validTypes.includes(value.type);
        })
        .test("fileSize", t('file_size_too_large'), (value) => {
            return value && value.size <= 5000000; // 5MB Limit
        }),
});

export function UploadRefundSlipModal({ isOpen, onClose, onUpload, loading }) {
    const {
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(schema(t)),
    });

    const { t } = useTranslation('order');

    const payment_slip = watch("payment_slip");

    const onSubmit = async (data) => {
        try {
            // Pass the file data to the parent handler
            await onUpload(data);
            reset();
            onClose();
        } catch (err) {
            Toaster.error(err?.message || t('failed_to_upload_payment_slip'));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setValue("payment_slip", file, { shouldValidate: true });
        }
    };

    return (
        <Dialog open={isOpen} handler={onClose} size="xs" className="rounded-2xl overflow-hidden">
            {loading && <ShowLoader />}

            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
                <Typography variant="h5" color="blue-gray" className="font-bold">
                    {t('upload_payment_slip_title')}
                </Typography>
                <button
                    onClick={onClose}
                    className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                    <XMarkIcon className="h-6 w-6 text-gray-500" />
                </button>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogBody className="px-6 py-6">
                    <div className="space-y-4">
                        <Typography className="text-sm text-gray-600">
                            {t('upload_proof_instruction')}
                        </Typography>

                        {/* Upload Zone */}
                        <div className="relative">
                            {!payment_slip ? (
                                <label
                                    htmlFor="slip-upload"
                                    className={`flex flex-col items-center justify-center w-full h-44 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${
                                        errors.payment_slip 
                                        ? "border-red-300 bg-red-50" 
                                        : "border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-green-400"
                                    }`}
                                >
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <CloudArrowUpIcon className={`w-10 h-10 mb-3 ${errors.payment_slip ? "text-red-400" : "text-gray-400"}`} />
                                        <p className="mb-2 text-sm text-gray-700 font-semibold"> 
                                            {t('click_to_upload')}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {t('file_types_and_size')}
                                        </p>
                                    </div>
                                    <input
                                        id="slip-upload"
                                        type="file"
                                        className="hidden"
                                        accept=".pdf, .jpg, .jpeg, .png"
                                        onChange={handleFileChange}
                                    />
                                </label>
                            ) : (
                                <div className="space-y-2">
                                    <FilePreview
                                        file={payment_slip}
                                        onRemove={() => setValue("payment_slip", null)}
                                        isView={false}
                                        containerClass="relative w-full h-44 rounded-2xl overflow-hidden border border-gray-200"
                                    />
                                    <Typography className="text-xs text-center text-gray-500 font-medium italic">
                                        {payment_slip.name}
                                    </Typography>
                                </div>
                            )}

                            {errors.payment_slip && (
                                <p className="text-red-500 text-xs mt-2 font-medium italic">
                                    {errors.payment_slip.message}
                                </p>
                            )}
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter className="flex justify-end gap-3 px-6 pb-6 pt-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-colors"
                    >
                        {t('cancel_button')}
                    </button>
                    <button
                        type="submit"
                        disabled={loading || !payment_slip}
                        className="flex-1 py-3 px-4 bg-[#10B981] hover:bg-emerald-600 text-white font-bold rounded-xl transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? t('uploading_button') : t('submit_slip_button')}
                    </button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}